import { Id, isIdMatch } from '@hero/schema';
import { type SelectionValue } from './types';

/**
 * Extracts Id from value (handles both Id and {_id: Id})
 */
function extractId(item: unknown): Id {
  if (typeof item === 'object' && item !== null && '_id' in item) {
    return (item as { _id: Id })._id;
  }
  return item as Id;
}

/**
 * Normalizes any value format to Id[]
 * Handles: Id, {_id: Id}, Id[], {_id: Id}[]
 * Always returns an array, even for single values
 */
export function normalizeValue(value: unknown): Id[] | undefined {
  if (!value) return undefined;

  // Handle array formats
  if (Array.isArray(value)) {
    return value.map(extractId);
  }

  // Handle single value - wrap in array
  return [extractId(value)];
}

/** Get id from item (for generic T). */
export type GetItemIdFn<T> = (item: T) => Id;

/**
 * Toggle selection when working with raw items T (list data).
 * Uses getItemId to get id from item.
 */
export function toggleSelectionItem<T>(
  item: T,
  currentIds: Id[],
  currentItems: T[],
  getItemId: GetItemIdFn<T>,
): { ids: Id[]; items: T[] } {
  const id = getItemId(item);
  const isSelected = currentIds.some(oid => isIdMatch(oid.toString(), id.toString()));

  if (isSelected) {
    return {
      ids: currentIds.filter(oid => !isIdMatch(oid.toString(), id.toString())),
      items: currentItems.filter(i => !isIdMatch(getItemId(i).toString(), id.toString())),
    };
  }

  return {
    ids: [...currentIds, id],
    items: [...currentItems, item],
  };
}

/**
 * Replace selection with a single item (single mode).
 */
export function replaceSelectionItem<T>(
  item: T,
  getItemId: GetItemIdFn<T>,
): { ids: Id[]; items: T[] } {
  return {
    ids: [getItemId(item)],
    items: [item],
  };
}

/**
 * Normalizes SelectionValue to { ids, items }. Items are only set when full objects (T with more than _id) were passed.
 */
export function normalizeSelectionValue<T extends { _id: Id }>(
  value: SelectionValue<T> | undefined,
): { ids: Id[]; items: T[] } {
  if (value == null) return { ids: [], items: [] };
  if (Array.isArray(value)) {
    if (value.length === 0) return { ids: [], items: [] };
    const first = value[0];
    if (typeof first === 'object' && first !== null && '_id' in first) {
      const arr = value as { _id: Id }[];
      const ids = arr.map(i => i._id);
      const hasFullItems = Object.keys(first).length > 1;
      return { ids, items: hasFullItems ? (arr as T[]) : [] };
    }
    return { ids: value as Id[], items: [] };
  }

  if (typeof value === 'object' && value !== null && '_id' in value) {
    const obj = value as T;
    const hasFullItem = Object.keys(obj).length > 1;
    return { ids: [obj._id], items: hasFullItem ? [obj] : [] };
  }

  return { ids: [value as Id], items: [] };
}

export function defaultGetItemId<T>(item: T): Id {
  const any = item as Record<string, unknown>;
  return (any._id ?? any.id) as Id;
}
